import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { MaterialModule } from 'src/app/material.module';
import { BaseRespone } from 'src/app/models/common.response';
import { AbhaOtpResendComponent } from '../abha-otp-resend/abha-otp-resend.component';
import { AbhaService } from '../abha.service';
import { AbhaOtpResult } from '../iabha-info';

const OTP_PATTERN = /^[0-9]{6}$/;

// Verifies the communication mobile of a new ABHA when it is not the Aadhaar mobile
// (NHA M1 CRT_ABHA_109). Emits the transaction id the next step continues with.
@Component({
  selector: 'app-abha-mobile-verify',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, TranslateModule, AbhaOtpResendComponent],
  templateUrl: './abha-mobile-verify.component.html',
})
export class AbhaMobileVerifyComponent {
  @Input({ required: true }) txnId!: string;
  @Input({ required: true }) mobile!: string;
  @Output() readonly verified = new EventEmitter<string>();
  @Output() readonly skipped = new EventEmitter<void>();

  readonly otpControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.pattern(OTP_PATTERN)],
  });
  otpTxnId = '';
  isBusy = false;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private abhaService: AbhaService,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) { }

  get isOtpSent(): boolean {
    return this.otpTxnId.length > 0;
  }

  onSendOtp(): void {
    this.isBusy = true;
    this.abhaService
      .requestMobileOtp({ txnId: this.txnId, mobile: this.mobile })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => this.handleOtpResponse(response));
  }

  onVerify(): void {
    if (this.otpControl.invalid) {
      this.otpControl.markAsTouched();
      return;
    }

    this.isBusy = true;
    this.abhaService
      .verifyMobileOtp({ txnId: this.otpTxnId, otp: this.otpControl.value })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => this.handleVerifyResponse(response));
  }

  private handleOtpResponse(response: BaseRespone<AbhaOtpResult>): void {
    this.isBusy = false;
    if (response && response.isSuccess && response.result?.txnId) {
      this.otpTxnId = response.result.txnId;
      this.toastr.success(this.translate.instant('ABHA.MOBILE_OTP_SENT', { mobile: this.mobile }));
      return;
    }

    this.toastr.error(this.resolveError(response, 'ABHA.OTP_FAILED'));
  }

  private handleVerifyResponse(response: BaseRespone<AbhaOtpResult>): void {
    this.isBusy = false;
    if (response && response.isSuccess && response.result) {
      this.toastr.success(this.translate.instant('ABHA.MOBILE_VERIFIED'));
      this.verified.emit(response.result.txnId || this.txnId);
      return;
    }

    this.toastr.error(this.resolveError(response, 'ABHA.MOBILE_VERIFY_FAILED'));
  }

  private resolveError(response: BaseRespone<unknown> | null, fallbackKey: string): string {
    if (response && response.errors && response.errors.length > 0) {
      return response.errors.join('\n');
    }

    return response?.message || this.translate.instant(fallbackKey);
  }
}
